import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import locales from 'locale';

const LocaleContext = createContext();

const languages = Object.keys(locales);
const isVariantLocale = (locale) => typeof locale === 'string' && locale.includes('-x-');
const getBaseLocale = (locale) => locale?.split('-x-')[0];

const getDefaultLanguage = () => {
  const storageLanguage = localStorage.getItem('locale');

  if (storageLanguage) {
    return storageLanguage;
  } else {
    return 'en';
  }
};

const LocaleProvider = ({ children }) => {
  const { i18n, t: translate } = useTranslation();
  const i18nReference = useRef(i18n);
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegments = useMemo(
    () => location.pathname.split('/').filter(Boolean),
    [ location.pathname ]
  );
  const pathLocale = pathSegments[0];
  const currentLocale = location.pathname.includes('/terms') ? undefined : pathLocale;
  const merchantIndex = pathSegments.indexOf('merchant');
  const pathId = merchantIndex >= 0 ? pathSegments[merchantIndex + 1] : undefined;

  useEffect(() => {
    i18nReference.current = i18n;
  }, [ i18n ]);

  const persistLocale = useCallback((locale) => {
    localStorage.setItem('locale', locale);
    i18nReference.current.changeLanguage(locale);
  }, []);

  const getBrowserLocaleCandidates = useCallback(() => {
    const browserLanguages = navigator.languages?.length
      ? navigator.languages
      : [ navigator.language ];

    return [ ...new Set(browserLanguages
      .filter((locale) => typeof locale === 'string' && locale.trim())
      .flatMap((locale) => {
        const normalizedLocale = locale.trim().replaceAll('_', '-').toLowerCase();
        const baseLocale = normalizedLocale.split('-')[0];

        return normalizedLocale === baseLocale
          ? [ normalizedLocale ]
          : [ normalizedLocale, baseLocale ];
      })) ];
  }, []);

  const changeLocale = useCallback((locale, { replace = false } = {}) => {
    if (!locale || currentLocale === locale) {
      return;
    }

    const baseLocale = isVariantLocale(locale) ? getBaseLocale(locale) : locale;
    const destination = pathId
      ? `/${baseLocale}/merchant/${pathId}`
      : `/${baseLocale}`;

    navigate(destination, { replace });
  }, [ currentLocale, navigate, pathId ]);

  useEffect(() => {
    if (location.pathname.includes('/terms')) {
      return;
    }

    if (!pathLocale) {
      navigate(`/${getDefaultLanguage()}`);
      return;
    }

    if (!isVariantLocale(pathLocale)) {
      persistLocale(pathLocale);
    }
  }, [ location.pathname, navigate, pathLocale, persistLocale ]);

  return (
    <LocaleContext.Provider
      value={{
        locale: currentLocale,
        locales: languages,
        changeLocale,
        getBrowserLocaleCandidates,
        persistLocale,
        translate,
        isVariantLocale,
        getBaseLocale,
        availableLanguages: languages
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

const LocaleConsumer = LocaleContext.Consumer;

const useLocaleContext = () => {
  const context = useContext(LocaleContext);

  if (context === undefined) {
    throw new Error('useLocaleContext must be used within a LocaleProvider');
  }

  return context;
};

export {
  LocaleContext, LocaleProvider, LocaleConsumer, useLocaleContext
};
